import { type AspectRatioProps, type BoxProps } from '@chakra-ui/react'
import type { Nullable, Option } from './general'
import {
  type Language,
  type PaymentMethodLogosConfig,
  type PaymentProviderBriqpayOptions,
  type PaymentProviderKlarnaOptions,
  type PaymentProviderLedyerOptions,
  type PaymentProviderName,
  type PaymentProviderNexiOptions,
  type PaymentProviderQliroOptions,
  type PaymentProviderSveaOptions,
  type ShippingProviderName,
  type SocialMedia,
  type SortingOption,
  type StoreAnalytics,
  type StorePrivacy,
  type TopNavPanelItem
} from './storefront'
import type { IngridCreateSessionRequest } from './ingrid'
import {
  type PostNordConditionalDeliveryType,
  type PostNordCost,
  type PostNordCustomer,
  type PostNordWarehouse
} from './postnord'
import { type CountryCode } from './countries'
import { type Ratio } from './widgets'
// Type-only, and erased at compile — `shipping.ts` imports `VatTreatment` back.
import type { DefaultShippingOptions } from './shipping'

export interface DbLogFields {
  created?: Option<number | string>
  updated?: Option<number | string>
}

export type DbInfoFields = DbLogFields & {
  id: string
  createdBy: Option<string>
  updatedBy: Option<string>
}

export interface MetaFields {
  metaTitle: Option<string>
  metaDescription: Option<string>
  metaHeader: Option<string>
}

export type CategoryDTO = {
  additionalText: Option<string>
  description: Option<string>
  id: string
  imgUrl: string
  name: string
  order: number
  slug: string
  subCategories?: Option<CategoryDTO[]>
  thumbnailDescription: string
  thumbnailUrl: string
} & MetaFields

export type FilterOptionTextValues = Option<string[]>
export type FilterOptionNumberValues = Option<number[]>

export interface FilterOptionDTO {
  label: Option<string>
  name: string
  numberValues: FilterOptionNumberValues
  textValues: FilterOptionTextValues
  variant: boolean
}

export interface ServiceDTO {
  id: string
  name: string
  description: string
  price: number
  discountPrice: number
}

export interface CheckoutService {
  quantity: number
  totalAmount: number
  service: ServiceDTO
}

export enum TagType {
  BADGE = 'BADGE',
  REGULAR = 'REGULAR'
}

export interface TagLightDTO {
  id: string
  imgUrl: string
  name: string
  slug: string
  type: TagType
}

export type TagDTO = {
  description: string
  thumbnailDescription: string
  thumbnailUrl: string
} & DbInfoFields &
  TagLightDTO &
  MetaFields

export interface ChannelDTO {
  code: string
  domain: string
  host: string
  languageCode: Language
  currency: Currency
  language: string
  /**
   * Masked cache-revalidation secret on the storefront-admin full Store read
   * (`{ masked }`, or null when unset). The raw value is never serialized — edit
   * it via PUT .../settings/channel. See FEATURE_CACHE_REVALIDATION.
   */
  revalidationSecret?: Option<SecretValue>
}

export interface DeliveryDTO {
  id: string
  name: string
  label: string
  buyable: boolean
}

export interface PickupDTO {
  id: string
  name: string
  address: {
    // Street name + house number
    address1?: string
    address2?: string
    country?: CountryCode
    mobilePhone?: string
    postalCode?: string
    // City
    postalRegion?: string
    attention?: string
    reference?: string
  }
  defaultPickup: boolean
}

export interface AssistantDTO {
  id: string
  company: string
  imgUrl: string
  name: string
}

export type EmbeddedAssistantSettings = Omit<AssistantDTO, 'id'>

/**
 * Write-only secret wire type used by the storefront-admin settings surface.
 * Read returns only `masked`; write sets via `newValue` or removes via `isClear`.
 */
export interface SecretValue {
  masked?: string
  newValue?: string
  isClear?: boolean
}

/**
 * `Channel` host + revalidation secret — edited via GET/PUT `.../settings/channel`.
 * Lives on the embedded `Channel` (not in the `settings` block). `host` is a plain
 * string in both directions; `revalidationSecret` is masked on read and
 * set/cleared on write, never returned in plaintext. See FEATURE_CACHE_REVALIDATION.
 */
export interface ChannelSettingsDTO {
  host?: string
  revalidationSecret?: Option<SecretValue>
}

export interface ShoppingAssistantEmbeddedSettings {
  apiKey?: Option<SecretValue>
  allowedOrigins?: string[]
}

/** `Store.settings.shoppingAssistant` — edited via PUT .../settings/shopping-assistant. */
export interface ShoppingAssistantSettings {
  enabled: boolean
  id?: string
  name?: string
  company?: string
  imgUrl?: string
  instructions?: string
  embedded?: Option<ShoppingAssistantEmbeddedSettings>
}

export interface NavigationPath {
  label: string
  name: string
  path: string
}

export interface NavigationDTO {
  links: NavigationPath[]
  paths: NavigationPath[]
  showCart: boolean
}

export interface SearchDTO {
  priceBoundaries: number[]
  sortOptions: SortingOption[]
  sliderStep: number
}

export interface LayoutTopNavHeightDTO {
  panel:
    | {
        base?: string | number
        sm?: string | number
      }
    | string
    | number
  content:
    | {
        base?: string | number
        sm?: string | number
      }
    | string
    | number
  spacer:
    | {
        base?: string | number
        sm?: string | number
      }
    | string
    | number
  hero?:
    | {
        base?: string | number
        sm?: string | number
        md?: string | number
      }
    | string
    | number
  bar?:
    | {
        base?: string | number
        sm?: string | number
        md?: string | number
      }
    | string
    | number
  menu?:
    | {
        base?: string | number
        sm?: string | number
        md?: string | number
      }
    | string
    | number
}

/**
 * Device tiers follow the store theme breakpoints, matching the search-list tablet columns:
 * mobile = below `sm`, tablet = `sm` up to `md`, desktop = `md` and up.
 */
export interface AihubLayoutConfig {
  showOnDesktop?: boolean
  showOnTablet?: boolean
  showOnMobile?: boolean
}

export interface LayoutDTO {
  desktopItemHeight: string
  mobileItemHeight: string
  menuMaxContentWidth?: BoxProps['maxW']
  maxContentWidth?: BoxProps['maxW']
  productListItemImageAspectRatio?: AspectRatioProps['ratio']
  productListItemTitleLines?: number
  collapsibleDiscountCodeForm?: boolean
  topNavHeight: LayoutTopNavHeightDTO
  topNavPanel?: {
    items: TopNavPanelItem[]
  }
  menuCategoriesThumbnails?: boolean
  /** Show the account nav item in the top navigation. Omitted/undefined = shown; only `false` hides it. */
  showAccountNav?: boolean
  /** Show the search bar in the top navigation. Omitted/undefined = shown; only `false` hides it. */
  showSearchNav?: boolean
  menuCategoriesConfig?: {
    desktop?: {
      variant?: 'standard' | 'multi-column'
    }
    mobile?: {
      variant?: 'standard' | 'multi-column'
    }
  }
  footerConfig?: {
    maxColumns?: number
    /**
     * Show the payment logos strip in the footer. Omitted/undefined = shown; only `false`
     * hides it — for sites that take no payments and so have nothing to advertise.
     */
    showPaymentLogo?: boolean
  }
  /**
   * Per-device visibility of the AI hub (assistant chat + offers drawer). Can only hide a hub
   * the store has enabled — enabling stays with the env flags and shopping-assistant settings.
   * Omitted/undefined = shown; only `false` hides it on that device.
   */
  aihub?: AihubLayoutConfig
  searchProductList?: {
    variant?: 'vertical-filter' | 'horizontal-filter'
    columnsNumber?: string | number
    columnsNumberTablet?: string | number
    columnsNumberMobile?: string | number
    imageAspectRatio?: Ratio
    imageAspectRatioTablet?: Ratio
    imageAspectRatioMobile?: Ratio
  }
  searchQueryMinLength?: number
  fonts?: LayoutFontsDTO
  vatToggle?: {
    enabled: boolean
    labelIncludedVat?: string
    labelExcludedVat?: string
  }
  /**
   * Label of the price-on-request button ("Offertförfrågan") rendered instead of
   * add-to-cart. Blank/omitted falls back to the `productRequest` translation
   * label, and finally to the hardcoded Swedish default.
   */
  productRequestLabel?: string
  /**
   * Which payment-method marks to advertise in the footer and on the product page.
   *
   * Layout rather than `payment`: the strip is display-only chrome, it says nothing
   * about which provider processes the order (with an orchestrator like Briqpay the
   * active provider cannot even imply it), and a store switching provider should keep
   * its logo configuration. It is also written by the same whole-document PUT as the
   * rest of this block, so the editor needs no second section write.
   */
  paymentMethodLogos?: PaymentMethodLogosConfig
  /** Merchant-authored copy nudging shoppers toward a product's volume tiers. */
  quantityDiscountDisplay?: QuantityDiscountDisplayConfig
  publicFolderPath?: string
}

/**
 * The three surfaces a quantity-discount nudge can appear on. Each is
 * configured independently — a merchant may want a loud PDP block and nothing
 * at all on a dense product grid.
 */
export interface QuantityDiscountDisplayConfig {
  /** Product grid cards: list, category, search, related. */
  listItem?: QuantityDiscountSurfaceConfig
  /** Product detail page, rendered above the add-to-cart controls. */
  pdp?: QuantityDiscountSurfaceConfig
  /** Per line in the slide-out cart drawer. */
  cart?: QuantityDiscountSurfaceConfig
  /**
   * Per line on the checkout page. Separate from `cart` on purpose: it is the
   * last place a shopper can still change a quantity, and the line there has a
   * stepper right beside it, so a merchant may well want louder copy here than
   * in the drawer.
   */
  checkout?: QuantityDiscountSurfaceConfig
}

/**
 * Optional extra lines the default copy renders *under* its main sentence.
 * Ignored once the merchant authors a template of their own.
 *
 * Only additive lines are toggleable. The percentage and the countdown to the
 * next tier live inside the main sentence — that sentence is the unit of
 * translation, so there is no way to lift a number out of it without handing
 * translators fragments to assemble. A merchant who wants those gone edits the
 * template.
 */
export interface QuantityDiscountFieldsConfig {
  /** A line with the per-unit price once the next tier lands. */
  nextUnitPrice?: boolean
  /** A line with what the shopper keeps per unit by reaching the next tier. */
  saving?: boolean
  /** The whole ladder as a row of pills. */
  tiers?: boolean
}

/**
 * Copy is authored as HTML with `{{token}}` placeholders, the same mechanism the
 * MAP_LOCATOR popover uses: values are HTML-escaped before substitution and the
 * assembled result is sanitised again on render.
 *
 * Three templates because the message genuinely differs by state and a single
 * string cannot branch. Any left blank falls back to a default built from
 * `fields`.
 */
/**
 * Which of the three states this surface is allowed to speak in. A key left
 * undefined is on — existing configurations predate this and must keep working.
 *
 * Separate from `templates` on purpose: blanking a template used to be the way
 * to silence a state, but "cleared" and "never set" are the same keystroke in a
 * text editor, so untouched states went silent by accident. Hiding is a
 * decision, and it gets its own switch.
 */
export interface QuantityDiscountStatesConfig {
  /** Below the first tier — the offer as an invitation. */
  teaser?: boolean
  /** A better tier is still reachable. */
  progress?: boolean
  /** Top tier reached; nothing left to unlock. A common one to hide. */
  achieved?: boolean
}

export interface QuantityDiscountSurfaceConfig {
  enabled: boolean
  states?: QuantityDiscountStatesConfig
  templates?: {
    /** Quantity is below the first tier — the offer is still an invitation. */
    teaser?: string
    /** Between tiers, or a `PACK` line with units sitting at full price. */
    progress?: string
    /** At or above the top tier — nothing left to unlock. */
    achieved?: string
  }
  fields?: QuantityDiscountFieldsConfig
}

export interface LayoutFontsDTO {
  heading?: string
  body?: string
  slogan?: string
}

export interface SettingsDTO {
  assistant: Option<AssistantDTO>
  shoppingAssistant?: Option<ShoppingAssistantSettings>
  shipping?: {
    freeShippingOver?: Option<number>
    deliveryFee?: Option<number>
    freeDeliveryLabel?: Option<string>
    paidDeliveryLabel?: Option<string>
    /**
     * Conditional delivery options for the carrier-less branch. Store-level,
     * NOT a `providers[]` entry: `ShippingProvider` on the backend is the enum
     * behind `Order.shippingProvider` and accepts POSTNORD | NSHIFT | INGRID
     * only — a `DEFAULT` entry is rejected on the write.
     */
    defaultOptions?: Option<DefaultShippingOptions>
    providers?: {
      name: ShippingProviderName
      ingridOptions: Option<{
        purchaseCountry?: IngridCreateSessionRequest['purchase_country']
        purchaseCurrency?: IngridCreateSessionRequest['purchase_currency']
      }>
      postnordOptions: Option<{
        applicationId: number
        customerKey: PostNordCustomer['customerKey']
        companyName?: string
        conditionalDeliveryTypes?: PostNordConditionalDeliveryType[]
        availableCountries?: CountryCode[]
        warehouses: PostNordWarehouse[]
        costs: PostNordCost[]
      }>
      nshiftOptions: Option<{
        availableCountries?: CountryCode[]
      }>
      /** The store-admin editor writes every provider's config under this
       *  single generic key; readers accept either. */
      options?: Option<Record<string, unknown>>
    }[]
  }
  payment?: {
    providers?: {
      name: PaymentProviderName
      klarnaOptions?: Option<PaymentProviderKlarnaOptions>
      nexiOptions?: Option<PaymentProviderNexiOptions>
      sveaOptions?: Option<PaymentProviderSveaOptions>
      qliroOptions?: Option<PaymentProviderQliroOptions>
      briqpayOptions?: Option<PaymentProviderBriqpayOptions>
      ledyerOptions?: Option<PaymentProviderLedyerOptions>
    }[]
  }
  /**
   * Store-level settings for the B2B invoice flow. The invoice flow has no
   * third-party payment provider, but it needs settings parallel to
   * `payment.providers` — currently just `countryCode`, used to derive the
   * shipping VAT rate at submit time via `getVatRateByCountryCode`.
   */
  invoice?: {
    countryCode?: string
  }
  layout: LayoutDTO
  logoUrl: Option<string>
  iconUrl?: Option<string>
  redirectsFileUrl?: Option<string>
  navigation: Option<NavigationDTO>
  search: Option<SearchDTO>
  theme: Record<string, any>
  socialMedia: Option<{ platforms: { name: SocialMedia; label: string; link: string }[] }>
  analytics: Option<StoreAnalytics>
  privacy: Option<StorePrivacy>
  manifest?: {
    name?: string
    shortName?: string
    description?: string
    themeColor?: string
    backgroundColor?: string
    lang?: string
    dir?: string
  }
}

export enum StoreType {
  B2B = 'B2B',
  B2C = 'B2C',
  MARKETPLACE = 'MARKETPLACE'
}

export interface StoreDTO {
  id: string
  type: StoreType
  channel: ChannelDTO
  companyId: string
  settings: Option<SettingsDTO>
  brands: Option<BrandDTO[]>
  categories: Option<CategoryDTO[]>
  tags: Option<TagDTO[]>
  services: Option<ServiceDTO[]>
  deliveries: Option<DeliveryDTO[]>
  pickups: Option<PickupDTO[]>
}

export enum RelatedProductTypes {
  STANDARD = 'STANDARD',
  CUSTOM = 'CUSTOM'
}

export interface RelatedProductDTO {
  customRelationType: string
  relatedProductId: string
  type: RelatedProductTypes
}

export enum ProductMediaType {
  DOCUMENT = 'DOCUMENT',
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO',
  LINK = 'LINK'
}

export type ProductMedia = {
  main: boolean
  name: Option<string>
  type: ProductMediaType
  url: string
  groupBy: Option<string>
} & Omit<DbInfoFields, 'id'>

export interface ProductImages {
  mainImageUrl: string
  mainImages?: ProductMedia[]
  secondaryImages?: ProductMedia[]
  secondaryImageUrl?: string
}

export enum ProductType {
  BUNDLE = 'BUNDLE',
  CONFIGURABLE = 'CONFIGURABLE',
  CONFIGURATION = 'CONFIGURATION',
  REGULAR = 'REGULAR'
}

export interface BundleDTO {
  productId: string
  quantity: number
}

export interface BundleFullDTO {
  product: OldProductDTO
  quantity: number
}

export interface ProductContentDTO {
  hidden: boolean
  name: string
  value: string
}

export interface ProductPrice {
  price: Option<number>
  originalPrice: Option<number>
  priceWithoutVat: Option<number>
  originalPriceWithoutVat: Option<number>
}

/**
 * How a quantity-discount ladder is scored against a line's quantity.
 *
 * - `THRESHOLD` — the highest tier the quantity reaches governs **every** unit
 *   on the line. Tiers never combine.
 * - `PACK` — units are grouped into blocks of `minQuantity`; each complete
 *   block takes that tier's percentage and the leftover units stay at full
 *   price. Blocks are filled greedily, largest tier first, and repeat.
 */
export type QuantityDiscountMode = 'THRESHOLD' | 'PACK'

export interface QuantityDiscountTierDTO {
  /** `THRESHOLD`: smallest qty that activates the tier. `PACK`: block size. Always >= 1. */
  minQuantity: number
  /** Percentage off the unit price, e.g. `5` for 5%. */
  percentage: number
}

/**
 * Volume-pricing configuration for one product on one channel, authored in
 * Pricing Optimisation and cached verbatim by the storefront backend.
 *
 * SF never applies it — the discount depends on a quantity that only exists on
 * the FE, so the amount is computed here via `allocateQuantityDiscount`. It
 * compounds **on top of** whatever unit price the surface already resolved
 * (public, campaign SALE, or a B2B subscriber's negotiated price), and on top
 * of a cart discount code.
 *
 * `null` when the product carries no offer — the empty-`tiers` form exists only
 * on the wire, as Pricing's signal to clear the backend's cache.
 * @see CONNECT_INTEGRATION_GUIDE.md §6.3 for the canonical allocation algorithm.
 */
export interface QuantityDiscountDTO {
  mode: QuantityDiscountMode
  /** Ascending by `minQuantity`, strictly monotonic in `percentage`. */
  tiers: QuantityDiscountTierDTO[]
}

export type ProductLightDTO = {
  active?: boolean
  articleNumber: string
  brand: Option<BrandLight>
  categoryIds: Option<string[]>
  configurations: Option<ProductConfiguration[]>
  deliveryId: string
  id: string
  medias: Option<ProductMedia[]>
  name: string
  priceRequest: boolean
  /**
   * Volume tier ladder from Pricing, returned verbatim and never applied
   * server-side. Null when the product carries no offer. Rides every product
   * read *except* autocomplete, whose projection omits it.
   */
  quantityDiscount?: Option<QuantityDiscountDTO>
  serviceIds: Option<string[]>
  slug: string
  // True when the subscriber price-list overlay matched a row for this product,
  // so `price` is the shopper's negotiated price rather than the public one.
  // Never derive this by comparing `price` to `originalPrice` — a campaign price
  // differs with no subscriber involved, and a negotiated price that equals the
  // public one would read as false.
  subscriberPrice?: boolean
  tags: Option<TagLightDTO[]>
  type: ProductType
  vatRate?: number
} & DbInfoFields &
  ProductPrice

export type OldProductDTO = {
  active: boolean
  bundles: Option<BundleDTO[]>
  categoryIds: Option<string[]>
  channel: string
  description: string
  grandId: string
  geckoId: string
  gtin: string
  inStock: boolean
  main: boolean
  options: Option<FilterOptionDTO[]> | null
  popular: boolean
  publishedBy: Option<string>
  relatedProducts: Option<RelatedProductDTO[]>
  source: string
  specifications: Option<ProductContentDTO[]>
  stockLevel: Option<number>
  storeId: string
  tagIds: Option<string[]>
  variantGroup: Option<string>
} & MetaFields &
  ProductLightDTO

export interface ProductConfiguration {
  optional: boolean
  position: number
  variantGroupId: Option<string>
  label: Option<string>
}

export interface StoreProductFilterDTO {
  articleNumbers?: string[]
  brandIds?: string[]
  categoryIds?: string[]
  customerId?: string
  filters?: Partial<FilterDTO>[]
  gtins?: string[]
  maxPrice?: number
  minPrice?: number
  names?: string[]
  productIds?: string[]
  search?: string
  tagIds?: string[]
  types?: ProductType[]
}

export type StoreSearchProductFilterDTO = Pick<
  StoreProductFilterDTO,
  'brandIds' | 'categoryIds' | 'customerId' | 'maxPrice' | 'minPrice'
> & {
  optionIds?: string[][] // [['color1Id', 'color2Id'], ['size1Id', 'size2Id']]
  text: string // search query
}

export interface ProductSearchFacets {
  brands: SearchBrandFacetDTO[]
  categories: SearchCategoryFacetDTO[]
  options: SearchOptionFacetDTO[]
  prices: SearchPriceFacetDTO[]
  minPrice: number | null
  maxPrice: number | null
}

export interface SearchPriceFacetDTO {
  from: number
  to: number
  count: number
}

export interface SearchOptionValueFacetDTO {
  id: string
  value: string
  count: number
}

export interface SearchOptionFacetDTO {
  name: string
  values: SearchOptionValueFacetDTO[]
}

export interface SearchBrandFacetDTO {
  id: string
  name: string
  count: number
}

export type SearchCategoryFacetDTO = SearchBrandFacetDTO & {
  ancestors: string[] | null
}

export interface ProductSearchResponseDTO {
  facets: Nullable<ProductSearchFacets>
  products: ProductLightDTO[]
  totalElements: number
  totalPages: number
}

export interface ProductAutocompleteDTO {
  imageUrl: string
  name: string
  price: number
  originalPrice: number
  priceWithoutVat?: number
  originalPriceWithoutVat?: number
  slug: string
  subscriberPrice?: boolean
  // Not yet returned by the BE — the autocomplete shape is the one product-read
  // response that omits it. Optional so the purchasability gate stays inert until
  // it lands, then starts working with no further FE change.
  priceRequest?: boolean
}

export enum FilterType {
  TEXT = 'TEXT',
  NUMBER = 'NUMBER'
}

export interface FilterDTO {
  id?: string
  label: Option<string>
  name: string
  numberValues: number[]
  storeId?: string
  textValues: string[]
  type: FilterType
  visible: boolean
}

export interface OldStoreProductFilterDTO {
  brands: BrandLight[]
  options: Option<FilterOptionDTO[]>
}

export type DiscountDTO = {
  brandIds: Option<string[]>
  campaign: Option<boolean>
  categoryIds: Option<string[]>
  code: string
  name: string
  storeId: string
  tagIds: Option<string[]>
  value: number
} & DbInfoFields

export interface PaginatedResponse<T> {
  content: T
  totalElements: number
  totalPages: number
}

export type GetProductsRes = PaginatedResponse<ProductLightDTO[]>

export type GetProductsFullRes = PaginatedResponse<OldProductDTO[]>

export interface ErrorRes {
  error: string
}

export interface GetProductVariantsRes {
  variants: Option<OldProductDTO[]>
}

export interface RelatedProductItem {
  product: OldProductDTO
  related: RelatedProductDTO
}

export interface GetProductRelatedRes {
  related: RelatedProductItem[]
}

export interface GetProductBundlesRes {
  bundles: Option<BundleFullDTO[]>
}

export interface InitialProductsState {
  initialProductsState: GetProductsRes
  initialFilterState: Partial<StoreProductFilterDTO>
}

export interface StorefrontInitialProducts {
  state: InitialProductsState
}

export interface StorefrontRelatedProducts {
  relatedProducts: Option<ProductLightDTO[]>
}

export type BrandDTO = {
  description: string
  id: string
  imgUrl: string
  name: string
  slug: string
  thumbnailDescription: string
  thumbnailUrl: string
} & MetaFields

export interface BrandLight {
  id: string
  name: string
}

export interface BrandGroup {
  group: string
  children: Option<BrandDTO[]>
}

export enum Currency {
  DKK = 'DKK',
  EUR = 'EUR',
  GBP = 'GBP',
  NOK = 'NOK',
  SEK = 'SEK',
  'USD' = 'USD'
}

export interface CredentialsDTO {
  email: string
  password: string
}

export interface CreateCustomerDTO {
  email: string
  firstName: string
  lastName: string
  type: CustomerType
}

export enum Gender {
  FEMAILE = 'FEMALE',
  MALE = 'MALE',
  UNDEFINED = 'UNDEFINED'
}

export enum CustomerType {
  BUSINESS = 'BUSINESS',
  CONSUMER = 'CONSUMER'
}

export interface CustomerAddressDTO {
  address1: Option<string>
  address2: Option<string>
  attention: Option<string>
  country: Option<string>
  label: Option<string>
  main: boolean
  mobilePhone: Option<string>
  postalCode: Option<string>
  postalRegion: Option<string>
  reference: Option<string>
}

export interface PhoneDTO {
  label: Option<string>
  number: Option<string>
  prefix: Option<string>
}

/**
 * BUSINESS customers carry a hydrated `companyProfile` block populated from
 * the ecosystem `Subscriber` record. The FE reads `companyProfile.companyId`
 * to identify the buying company and the addresses/contacts/sales-terms for
 * B2B checkout. CONSUMER customers get `companyProfile = null`.
 */
export interface CompanyAddressDTO {
  id: string
  /** BILLING / SHIPPING / ORGANIZATION — open-ended on the wire. */
  type: string
  label: Option<string>
  attention: Option<string>
  address1: string
  address2: Option<string>
  postalCode: string
  postalRegion: string
  country: string
  email: Option<string>
  phone: Option<string>
  reference: Option<string>
  default: boolean
}

export interface CompanyContactDTO {
  contactId: string
  name: string
  title: Option<string>
  email: Option<string>
  phone: Option<string>
  notes: Option<string>
  primary: boolean
  /** FK into `companyProfile.addresses[].id`. Hydrated optionally on `address`. */
  addressId: Option<string>
  address: Option<CompanyAddressDTO>
}

/**
 * VAT classification of a customer, on `CompanyProfile.salesTerms.vatTreatment`.
 *
 * @remarks
 * Platform-neutral — each integration maps it to its own accounting system's
 * codes (Fortnox `VATType`, etc.). It classifies the *customer*; it does NOT
 * set per-line VAT rates, which stay on the order line's `vatRate`.
 *
 * Optional on the wire: when null, consumers derive it from the profile's
 * `country` + `vatNumber` relative to the tenant's own jurisdiction. Set it
 * only to override that derivation.
 *
 * | Value | Rate | Typical case |
 * |---|---|---|
 * | `DOMESTIC` | tenant's domestic rate | SE customer of an SE tenant |
 * | `EU_REVERSE_CHARGE` | 0% — buyer self-accounts | EU company with a VAT number |
 * | `EU_VAT` | destination-country rate (OSS) | EU buyer, no VAT number |
 * | `EXPORT` | 0% | non-EU customer |
 * | `DOMESTIC_REVERSE_CHARGE` | 0% — buyer self-accounts | SE construction scheme |
 */
export type VatTreatment =
  | 'DOMESTIC'
  | 'EU_REVERSE_CHARGE'
  | 'EU_VAT'
  | 'EXPORT'
  | 'DOMESTIC_REVERSE_CHARGE'

export interface CompanySalesTermsDTO {
  paymentTerms: Option<string>
  /** Explicit VAT classification for this customer. Null (the common case)
   *  means "derive from `CompanyProfile.country` + `vatNumber`" — see
   *  `resolveVatTreatment` in `ecosystem/orders`. */
  vatTreatment: Option<VatTreatment>
  /** Mirrors the SF `PaymentType` enum on `Order`. `PROFORMA` marks a company
   *  that prepays against a pro-forma document before fulfilment; the SF invoice
   *  flow resolves INVOICE vs PROFORMA from this field. */
  paymentType: Option<'INVOICE' | 'UPFRONT' | 'PROFORMA'>
  creditLimit: Option<number>
  maxWriteOff: Option<number>
  chargeInterestOnOverdue: boolean
  interestGraceDays: Option<number>
  glAccounts: {
    accountsReceivableAccount: Option<string>
    prepaymentAccount: Option<string>
    cashDiscountAccount: Option<string>
  }
}

export interface CompanyProfileDTO {
  /** Ecosystem company id (FK into the SF-side `subscriber` collection). */
  companyId: string
  legalName: string
  organizationNumber: Option<string>
  website: Option<string>
  country: Option<string>
  vatNumber: Option<string>
  currency: Option<string>
  addresses: CompanyAddressDTO[]
  contacts: CompanyContactDTO[]
  purchasingTerms: Option<unknown>
  salesTerms: Option<CompanySalesTermsDTO>
  /** ISO-8601 timestamps on this block (Customer-level timestamps are unix seconds). */
  created: Option<string>
  updated: Option<string>
}

export interface CustomerDTO {
  id: string
  activationToken: Option<string>
  active: boolean
  addresses: CustomerAddressDTO[]
  dateOfBirth: Option<string>
  email: string
  firstName: string
  gender: Gender
  lastLogin: Option<string>
  lastName: string
  middleName: Option<string>
  organizationName: Option<string>
  password: Option<string>
  phones: PhoneDTO[]
  storeId: string
  /** Hydrated company block for BUSINESS customers — null for CONSUMER. */
  companyProfile: Option<CompanyProfileDTO>
  /** The contact record (one of `companyProfile.contacts`) tied to this
   *  Customer's login identity. */
  companyContact: Option<CompanyContactDTO>
  type: CustomerType
}

export interface CustomerLightDTO {
  id: string
  firstName: string
  gender: Gender
  lastName: string
  organizationName: string
  storeId: string
  type: CustomerType
}

export interface CustomerUpdateDTO {
  addresses: Option<CustomerAddressDTO[]>
  dateOfBirth: Option<string>
  firstName: Option<string>
  gender: Option<Gender>
  lastName: Option<string>
  middleName: Option<string>
  organizationName: Option<string>
  phones: Option<PhoneDTO[]>
}

export interface CustomerAuthUpdateDTO {
  activationToken: Option<string>
  email: Option<string>
  newPassword: Option<string>
  oldPassword: Option<string>
}

export interface CreatCustomerDTO {
  email: string
  firstName: string
  lastName: string
  type: CustomerType
}

export interface MarketplaceProductDTO {
  articleNumber: string
  brand: string
  channel: string
  gtin: string
  imgUrl: Option<string>
  inStock: boolean
  name: string
  originalPrice: number
  price: number
  stockLevel: number
  url: Option<string>
}

export interface AppInfo {
  id: number
  name: string
  url: string
}
